import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PLATFORM_LABELS, istLocal } from "@/lib/smm";

const inputCls = "w-full rounded-lg border border-slate-200 p-2.5 text-sm focus:border-emerald-500 outline-none";
const TITLES = { edit: "Edit scheduled post", published: "Mark as published", failed: "Mark as failed" };

// mode: edit (time/platform/caption) | published (post link) | failed (reason)
export const PostActionDialog = ({ post, mode, onClose, onDone }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (post) setF({ when: istLocal(post.scheduled_at), platform: post.platform, caption: post.caption, post_url: post.post_url || "", error: "" });
  }, [post]);
  if (!post) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "edit") {
        const body = {};
        if (f.caption !== post.caption) body.caption = f.caption;
        if (f.when !== istLocal(post.scheduled_at)) body.scheduled_at = f.when;
        if (f.platform !== post.platform) body.platform = f.platform;
        await api.put(`/admin/smm/posts/${post.id}`, body);
      } else if (mode === "published") {
        await api.post(`/admin/smm/posts/${post.id}/published`, { post_url: f.post_url });
      } else {
        await api.post(`/admin/smm/posts/${post.id}/failed`, { error: f.error });
      }
      toast.success("Saved");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`smm-post-dialog-${mode}`}>
        <DialogHeader><DialogTitle>{TITLES[mode]}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 line-clamp-2">{post.title}</p>
        {mode === "edit" && (
          <>
            <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">Date & time (IST)</span>
              <input type="datetime-local" className={inputCls} value={f.when || ""} onChange={set("when")} data-testid="smm-post-when" /></label>
            <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">Platform</span>
              <select className={inputCls} value={f.platform} onChange={set("platform")} data-testid="smm-post-platform">
                {Object.entries(PLATFORM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></label>
            <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">Caption for this post</span>
              <textarea rows={7} className={inputCls} value={f.caption || ""} onChange={set("caption")} data-testid="smm-post-caption" /></label>
            <p className="text-[11px] text-slate-500">Changing the platform rewrites the caption for that platform.</p>
          </>
        )}
        {mode === "published" && (
          <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">Link to the live post (optional)</span>
            <input className={inputCls} placeholder="https://www.instagram.com/p/…" value={f.post_url} onChange={set("post_url")} data-testid="smm-post-url" /></label>
        )}
        {mode === "failed" && (
          <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">What went wrong?</span>
            <input className={inputCls} placeholder="e.g. Instagram rejected the image" value={f.error} onChange={set("error")} data-testid="smm-post-error" /></label>
        )}
        <button onClick={submit} disabled={busy || (mode === "failed" && f.error.trim().length < 2)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-lg disabled:opacity-60" data-testid="smm-post-submit">
          {busy ? "Saving…" : "Save"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
